import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { FileDown, FileSpreadsheet, FileText, Share2, HandCoins, ChevronDown, Search, Filter, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { DateFilters, filterParams } from "@/components/DateFilters";
import { API, useAuth } from "@/context/AuthContext";
import { CompanyHeaderDialog } from "@/components/CompanyHeaderDialog";
import { inr } from "@/pages/Dashboard";
import { toast } from "sonner";

const TYPE_BADGE = {
  Invoice: "bg-slate-100 text-slate-600", Retention: "bg-purple-100 text-purple-700",
  Advance: "bg-sky-100 text-sky-700", Payment: "bg-emerald-100 text-emerald-700", Receipt: "bg-emerald-100 text-emerald-700",
  "TDS Deducted": "bg-rose-100 text-rose-700",
};

export function LedgerStatement({ ptype, title, subtitle, partyLabel, testPrefix }) {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [parties, setParties] = useState([]);
  const [party, setParty] = useState(() => searchParams.get("party") || "");
  const [filters, setFilters] = useState({ from: null, to: null });
  const [basis, setBasis] = useState("posting");
  const [data, setData] = useState(null);
  const [advances, setAdvances] = useState(null);
  const [advOpen, setAdvOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Focus = arrived from Ageing with a bracket; show only those open docs until cleared.
  const [focus, setFocus] = useState(() => {
    const b = searchParams.get("bucket");
    return b ? { bucket: b, category: searchParams.get("category") || null } : null;
  });
  const [focusOnly, setFocusOnly] = useState(true);

  useEffect(() => {
    axios.get(`${API}/ledger/${ptype}/parties`).then((r) => {
      setParties(r.data);
      if (r.data.length) setParty((p) => p || r.data[0].code);
    }).catch(() => {});
  }, [ptype]);

  const load = useCallback(() => {
    if (!party) return undefined;
    const ctrl = new AbortController();
    axios.get(`${API}/ledger/${ptype}`, { params: { party, ...filterParams(filters), date_basis: basis }, signal: ctrl.signal })
      .then((r) => setData(r.data)).catch(() => {});
    return () => ctrl.abort();
  }, [ptype, party, filters, basis]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    if (!party) { setAdvances(null); return; }
    axios.get(`${API}/advance-requests/${ptype}`, { params: { party } })
      .then((r) => setAdvances(r.data)).catch(() => setAdvances(null));
  }, [ptype, party]);

  const download = async (format) => {
    try {
      const stmt = format !== "csv";
      const res = await axios.get(`${API}/ledger/${ptype}/${stmt ? "statement" : "export"}`, {
        params: { party, ...filterParams(filters), format, date_basis: basis }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = stmt ? `${ptype === "ap" ? "vendor" : "customer"}-statement-${party}.${format}` : `${ptype}-ledger-${party}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 200);
    } catch (e) {
      let msg = "Could not generate the statement. Please try again.";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* not JSON */ }
      toast.error(msg);
    }
  };

  const nat = data?.balance_nature || (ptype === "ap" ? "Cr" : "Dr");
  const term = search.trim().toLowerCase();
  const refMatch = (v) => {
    const s = String(v || "").toLowerCase();
    if (!/[*?]/.test(term)) return s === term;
    const re = new RegExp("^" + term.replace(/[.+^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*").replace(/\?/g, ".") + "$");
    return re.test(s);
  };
  const inFocus = (r) => r.open && r.bucket === focus.bucket && (!focus.category || r.category === focus.category);
  const focusActive = Boolean(focus && focusOnly);
  const searched = data
    ? (term
        ? data.rows.filter((r) => refMatch(r.doc_no) || refMatch(r.ref_no))
        : data.rows)
    : [];
  const visibleRows = focusActive ? searched.filter(inFocus) : searched;
  const focusCount = data && focus ? data.rows.filter(inFocus).length : 0;
  const focusDebit = visibleRows.reduce((s, r) => s + (focusActive ? r.debit : 0), 0);
  const focusCredit = visibleRows.reduce((s, r) => s + (focusActive ? r.credit : 0), 0);
  const hasFx = (data?.fx_summary?.length || 0) > 0;
  const fcFmt = (cur, v) => `${cur} ${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const focusLabel = focus ? `${focus.category ? `${focus.category} · ` : ""}${focus.bucket}` : "";

  return (
    <div className="space-y-6" data-testid={`${testPrefix}-page`}>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && <CompanyHeaderDialog testPrefix={testPrefix} />}
          <Button variant="outline" size="sm" onClick={() => download("pdf")} disabled={!party} data-testid={`${testPrefix}-export-pdf`}><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
          <Button variant="outline" size="sm" onClick={() => download("xlsx")} disabled={!party} data-testid={`${testPrefix}-export-xlsx`}><FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel</Button>
          <Button variant="outline" size="sm" onClick={() => download("csv")} disabled={!party} data-testid={`${testPrefix}-export-csv`}><FileDown className="h-4 w-4 mr-1.5" />CSV</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <SearchableSelect
          options={parties.map((p) => ({ value: p.code, label: p.name, sublabel: p.code }))}
          value={party}
          onChange={setParty}
          placeholder={`Select ${partyLabel}`}
          width="w-80"
          testId={`${testPrefix}-party-select`}
        />
        <DateFilters showWbs filters={filters} setFilters={setFilters} testPrefix={testPrefix} fromLabel="From date" toLabel="To date (blank = today)" />
        <div className="flex items-center rounded-md border border-slate-200 bg-white p-0.5 text-xs" data-testid={`${testPrefix}-date-basis`} title="Which SAP date the From / To dates, opening balance and order use">
          {[["posting", "Posting date"], ["document", "Document date"]].map(([k, l]) => (
            <button type="button" key={k} onClick={() => setBasis(k)} data-testid={`${testPrefix}-date-basis-${k}`}
              className={`rounded px-2.5 py-1 transition-colors ${basis === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>
        <div className="relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={ptype === "ap" ? "Exact SAP ref / supplier ref — * for wildcard" : "Exact SAP ref / invoice no — * for wildcard"}
            className="w-64 h-9 pl-8 pr-8 border-slate-300 text-sm bg-white"
            data-testid={`${testPrefix}-search-input`}
          />
          {search && (
            <button type="button" onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
              data-testid={`${testPrefix}-search-clear`}>✕</button>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-400 -mt-1">Leave "From date" blank to see all activity up to the "As on date" with a running balance.</p>

      {focus && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid={`${testPrefix}-focus-banner`}>
          <div className="flex items-center gap-2 text-sm text-slate-800">
            <Filter className="h-4 w-4 text-amber-600" />
            <span>From <Link to={`/${ptype}-ageing`} className="font-semibold underline decoration-amber-400 underline-offset-2">{ptype.toUpperCase()} Ageing</Link>:</span>
            <span className="rounded-full bg-slate-900 text-white text-xs font-semibold px-2.5 py-0.5" data-testid={`${testPrefix}-focus-chip`}>{focusLabel}</span>
            <span className="text-slate-500">{focusCount} open document{focusCount === 1 ? "" : "s"} in this bracket</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant={focusOnly ? "default" : "outline"} size="sm" onClick={() => setFocusOnly(true)} data-testid={`${testPrefix}-focus-only`}>Only these</Button>
            <Button variant={focusOnly ? "outline" : "default"} size="sm" onClick={() => setFocusOnly(false)} data-testid={`${testPrefix}-focus-full`}>Full statement</Button>
            <button type="button" onClick={() => setFocus(null)} className="text-slate-400 hover:text-slate-700" title="Clear" data-testid={`${testPrefix}-focus-clear`}><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      {!data ? (
        <div className="animate-pulse text-slate-400 text-sm">Loading statement...</div>
      ) : (
        <Card className="border-slate-200 shadow-sm fade-in-up">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="text-xl font-bold text-slate-900" data-testid={`${testPrefix}-party-name`}>{data.party.name}</CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  {partyLabel} Code: {data.party.code} · GSTIN: {data.party.gstin || "—"}
                  {data.party.msme === "Yes" && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold" data-testid={`${testPrefix}-party-msme`}>
                      MSME{data.party.msme_no ? ` · ${data.party.msme_no}` : ""}
                    </span>
                  )}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">Period: {data.from_date || "Beginning"} → {data.to_date || "Till Date"}</p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wider text-slate-400">Closing Balance</p>
                <p className="text-2xl font-black tracking-tight text-slate-900" data-testid={`${testPrefix}-closing`}>{inr(data.closing_balance)} <span className="text-sm text-slate-500">{nat}</span></p>
                <p className="text-xs text-slate-400 mt-0.5">Opening: {inr(data.opening_balance)} {nat}</p>
                {data.total_tds > 0 && (
                  <p className="text-xs font-semibold text-rose-600 mt-1" data-testid={`${testPrefix}-total-tds`}>Total TDS deducted: {inr(data.total_tds)}</p>
                )}
                {hasFx && data.fx_summary.map((e) => (
                  <p key={e.currency} className="text-xs font-semibold text-sky-700 mt-1" data-testid={`${testPrefix}-fx-closing-${e.currency}`}>
                    {e.currency} closing: {fcFmt(e.currency, e.closing_fc)} {nat} <span className="font-normal text-slate-400">(Dr {fcFmt(e.currency, e.debit_fc)} · Cr {fcFmt(e.currency, e.credit_fc)} in period)</span>
                  </p>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <Table data-testid={`${testPrefix}-table`}>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">{basis === "posting" ? "Posting Date" : "Document Date"}</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Inv.No. (SAP Ref)</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">{ptype === "ap" ? "Supplier Ref" : "Invoice No"}</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Reference</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Debit</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Credit</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Balance</TableHead>
                  {hasFx && <TableHead className="text-xs uppercase tracking-wider text-sky-700 text-right" data-testid={`${testPrefix}-fc-head`}>FC Amount</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {!focusActive && (
                <TableRow className="bg-slate-50/60">
                  <TableCell colSpan={7} className="text-sm font-semibold text-slate-600">Opening Balance</TableCell>
                  <TableCell className="text-right text-sm font-bold text-slate-800">{inr(data.opening_balance)} {nat}</TableCell>
                  {hasFx && <TableCell className="text-right text-xs text-sky-700">{data.fx_summary.map((e) => <div key={e.currency}>{fcFmt(e.currency, e.opening_fc)} {nat}</div>)}</TableCell>}
                </TableRow>
                )}
                {visibleRows.map((r, i) => (
                  <TableRow key={i} className={`border-b border-slate-100 hover:bg-slate-50/60 ${focus && !focusActive && inFocus(r) ? "bg-amber-50/70" : ""}`} data-testid={`${testPrefix}-row-${i}`}>
                    <TableCell className="text-sm text-slate-600">{r.date}{(() => { const o = basis === "posting" ? r.doc_date : r.posting_date; return o && o !== r.date ? <div className="text-[10px] text-slate-400" data-testid={`${testPrefix}-otherdate-${i}`}>{basis === "posting" ? "Doc" : "Posted"} {o}</div> : null; })()}</TableCell>
                    <TableCell className="text-sm font-mono text-slate-700">{r.doc_no}</TableCell>
                    <TableCell className="text-sm font-mono text-slate-700">{r.ref_no || "—"}</TableCell>
                    <TableCell>
                      <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${TYPE_BADGE[r.type] || "bg-slate-100 text-slate-600"}`}>{r.type}</span>
                      {r.open && r.bucket && r.type !== "Payment" && r.type !== "Receipt" && (
                        <span className="ml-1 text-[10px] text-slate-400" title="Ageing bracket (open item)">{r.bucket}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[220px] truncate">{r.reference || "—"}</TableCell>
                    <TableCell className="text-right text-sm text-slate-700">{r.debit ? inr(r.debit) : "—"}</TableCell>
                    <TableCell className="text-right text-sm text-slate-700">{r.credit ? inr(r.credit) : "—"}</TableCell>
                    <TableCell className="text-right text-sm font-semibold text-slate-900">{focusActive ? <span className="text-slate-300" title="Running balance shown in Full statement">—</span> : inr(r.balance)}</TableCell>
                    {hasFx && (
                      <TableCell className="text-right text-xs font-semibold text-sky-700 whitespace-nowrap" data-testid={`${testPrefix}-row-${i}-fc`}>
                        {r.fc_currency ? `${fcFmt(r.fc_currency, r.fc_debit || r.fc_credit)} ${r.fc_debit ? "Dr" : "Cr"}` : <span className="text-slate-300">—</span>}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {visibleRows.length === 0 && (
                  <TableRow><TableCell colSpan={hasFx ? 9 : 8} className="text-center text-sm text-slate-400 py-6">{focusActive ? `No open ${focusLabel} documents for this ${partyLabel.toLowerCase()}` : term ? "No transactions match your search" : "No transactions in this period"}</TableCell></TableRow>
                )}
                {focusActive ? (
                <TableRow className="bg-amber-50 font-bold" data-testid={`${testPrefix}-focus-totals`}>
                  <TableCell colSpan={5} className="text-sm text-slate-700 text-right">{focusLabel} — open total</TableCell>
                  <TableCell className="text-right text-sm text-slate-900">{inr(focusDebit)}</TableCell>
                  <TableCell className="text-right text-sm text-slate-900">{inr(focusCredit)}</TableCell>
                  <TableCell colSpan={hasFx ? 2 : 1}></TableCell>
                </TableRow>
                ) : (<>
                <TableRow className="bg-slate-50 font-bold">
                  <TableCell colSpan={5} className="text-sm text-slate-700 text-right">Period Totals</TableCell>
                  <TableCell className="text-right text-sm text-slate-900">{inr(data.total_debit)}</TableCell>
                  <TableCell className="text-right text-sm text-slate-900">{inr(data.total_credit)}</TableCell>
                  <TableCell></TableCell>
                  {hasFx && <TableCell className="text-right text-xs text-sky-700">{data.fx_summary.map((e) => <div key={e.currency}>Dr {fcFmt(e.currency, e.debit_fc)} · Cr {fcFmt(e.currency, e.credit_fc)}</div>)}</TableCell>}
                </TableRow>
                <TableRow className="bg-sky-50">
                  <TableCell colSpan={7} className="text-sm font-bold text-slate-800">Closing Balance</TableCell>
                  <TableCell className="text-right text-sm font-black text-slate-900">{inr(data.closing_balance)} {nat}</TableCell>
                  {hasFx && <TableCell className="text-right text-xs font-bold text-sky-700" data-testid={`${testPrefix}-fc-closing-cell`}>{data.fx_summary.map((e) => <div key={e.currency}>{fcFmt(e.currency, e.closing_fc)} {nat}</div>)}</TableCell>}
                </TableRow>
                </>)}
              </TableBody>
            </Table>
            <p className="text-xs text-slate-400 mt-3 flex items-center gap-1.5"><Share2 className="h-3.5 w-3.5" /> Export to PDF/Excel to share this statement of account with the {partyLabel.toLowerCase()}.</p>
          </CardContent>
        </Card>
      )}

      {advances && advances.count > 0 && (
        <Card className="border-amber-200 bg-amber-50/40 shadow-sm fade-in-up" data-testid={`${testPrefix}-advances-card`}>
          <button
            type="button"
            onClick={() => setAdvOpen((o) => !o)}
            data-testid={`${testPrefix}-advances-toggle`}
            className="w-full flex items-center justify-between px-6 py-4 text-left"
          >
            <div className="flex items-center gap-2.5">
              <HandCoins className="h-5 w-5 text-amber-600" />
              <div>
                <p className="text-sm font-bold text-slate-900">Pending Advance Requests ({advances.count})</p>
                <p className="text-xs text-slate-500">Down payment requests (Special GL "F") — not included in the balance above</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-black text-slate-900">{inr(advances.total)}</span>
              <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform duration-200 ${advOpen ? "rotate-180" : ""}`} />
            </div>
          </button>
          {advOpen && (
            <CardContent className="pt-0 pb-4">
              <Table data-testid={`${testPrefix}-advances-table`}>
                <TableHeader>
                  <TableRow className="bg-white/60">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Document No</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Date</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Due</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">WBS / PO</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {advances.items.map((it) => (
                    <TableRow key={it.doc_no} className="border-b border-amber-100" data-testid={`${testPrefix}-advance-row-${it.doc_no}`}>
                      <TableCell className="text-sm font-mono text-slate-700">{it.doc_no}</TableCell>
                      <TableCell className="text-sm text-slate-600">{it.date}</TableCell>
                      <TableCell className="text-sm text-slate-600">{it.due_date || "—"}</TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[220px] truncate">{[it.wbs_element, it.po_number].filter((x) => x && x !== "UNASSIGNED").join(" · ") || "—"}</TableCell>
                      <TableCell className="text-right text-sm font-semibold text-slate-900">{inr(it.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}

export default function APLedger() {
  return (
    <LedgerStatement
      ptype="ap"
      title="AP Ledger"
      subtitle="Vendor statement of account — invoices, payments and running balance for a period"
      partyLabel="Vendor"
      testPrefix="ap-ledger"
    />
  );
}
